import { useState, useEffect, useRef } from 'react'

import { FormInstance } from 'antd'
import { isEqual } from 'lodash'

interface UseFormWatcherOptions {
  interval?: number
  enabled?: boolean
}

/**
 * Custom hook to efficiently watch form field values using optimized polling
 * This is more efficient in very large forms than something like useWatch that triggers on every change
 * @param fields Array of field paths to watch
 * @param form Ant Design form instance
 * @param options Configuration options
 * @returns Object containing the watched field values
 */
export function useIntervalWatch(
  fields: string[],
  form?: FormInstance,
  options: UseFormWatcherOptions = {}
) {
  const { interval = 1000, enabled = true } = options
  const [watchedValues, setWatchedValues] = useState<Record<string, unknown>>(
    {}
  )
  const previousValuesRef = useRef<Record<string, unknown>>({})
  const timeoutRef = useRef<NodeJS.Timeout>(undefined)

  useEffect(() => {
    if (!enabled || fields.length === 0) return

    const getCurrentValues = () => {
      const { newValues, hasChanges } = fields.reduce<{
        newValues: Record<string, unknown>
        hasChanges: boolean
      }>(
        (acc, field) => {
          const value = form?.getFieldValue(field)
          acc.newValues[field] = value
          // Only check equality if we haven't found changes yet
          if (!acc.hasChanges) {
            acc.hasChanges = !isEqual(value, previousValuesRef.current[field])
          }
          return acc
        },
        { newValues: {}, hasChanges: false }
      )

      if (hasChanges) {
        previousValuesRef.current = newValues
        setWatchedValues(newValues)
      }

      // Schedule next check
      timeoutRef.current = setTimeout(getCurrentValues, interval)
    }

    // Get initial values and start polling
    getCurrentValues()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [fields, form, interval, enabled])

  return watchedValues
}
